import React, { useState, useEffect, useCallback } from 'react'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import { Container } from 'reactstrap'
import { EventActionsContext } from './EventActionsContext'
import moment from 'moment'
// LAP-733 (GE022) — load all 5 supported moment locales so the calendar can
// be rendered in any of fr/en/de/it/es. Default English is built-in.
import 'moment/locale/fr'
import 'moment/locale/de'
import 'moment/locale/it'
import 'moment/locale/es'
import { useTranslation } from 'react-i18next'

import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import { getColor } from './Colors'
import 'bootstrap/dist/css/bootstrap.min.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import './Calendar.scss'
import Year from './Year'
import YearEvent from './YearEvent'
import FormModal from './EventForm'
// LAP-769 — générateur de planning hebdomadaire (preview/validation/commit).
import GeneratePlanningModal from './GeneratePlanningModal'
// LAP-769 — paramétrage des contraintes de vie (horaires de travail, routine…).
import ConstraintsModal from './ConstraintsModal'
// LAP-614b — export de la période affichée vers un agenda externe.
import ExportCalendarModal from './ExportCalendarModal'
import { Event, MonthEvent, CustomAgendaEvent } from '../Components/CustomEvent'
import { makeGetRequest, makeGetAllRequest } from 'utils/api'
import { makePutRequest } from 'utils/api'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import TodayIcon from '@mui/icons-material/Today'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  // LAP-764 — polished header components, aligned with /Menu (PlanMeal)
  Typography,
  Stack,
  Chip,
  Paper,
} from '@mui/material'
// LAP-764 — icons for the redesigned action bar (mirrors the /Menu page)
import AddIcon from '@mui/icons-material/Add'
import EventRepeatIcon from '@mui/icons-material/EventRepeat' // LAP-769
import UndoIcon from '@mui/icons-material/Undo' // LAP-769 — annuler une génération
import TuneIcon from '@mui/icons-material/Tune' // LAP-769 — paramétrage contraintes
import SyncIcon from '@mui/icons-material/Sync'
import IosShareIcon from '@mui/icons-material/IosShare' // LAP-614b — export externe
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import FilterListIcon from '@mui/icons-material/FilterList'
import AddConnection from 'Components/UserOptionCard/AddConnection'

import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import CustomButton from 'Components/CustomButton/CustomButton'
import { Padding } from '@mui/icons-material'
import { makePostRequest } from 'utils/api'
import urls from 'utils/urls'
import useUrlAction from 'utils/urlAction'
import { useLocation, useNavigate } from 'react-router-dom'
// LAP-765 — icône pour le CTA "Conseiller temps" dans le header de la page.
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

const localizer = momentLocalizer(moment)

const DnDCalendar = withDragAndDrop(Calendar)

// LAP-764 — shared pill-button style, mirroring the /Menu (PlanMeal) header so
// the calendar toolbar feels visually consistent with the rest of the new UI.
const actionButtonSx = {
  borderRadius: 999,
  textTransform: 'none',
  fontWeight: 850,
  px: 1.8,
  color: '#ffffff',
  borderColor: 'rgba(255,255,255,0.22)',
  '&:hover': {
    borderColor: 'rgba(255,255,255,0.5)',
    bgcolor: 'rgba(255,255,255,0.08)',
  },
}

// LAP-764 — primary CTA variant (filled, accent blue) used for "Create event".
const primaryActionButtonSx = {
  ...actionButtonSx,
  bgcolor: '#1b75d0',
  color: '#ffffff',
  border: '1px solid rgba(27, 117, 208, 0.45)',
  '&:hover': { bgcolor: '#145da8', borderColor: 'rgba(27,117,208,0.65)' },
}

// LAP-764 — choisit un texte clair ou sombre selon la luminance perçue du
// fond, pour garder le titre des événements lisible quelle que soit la couleur
// de catégorie (certaines sont claires : cyan, menthe… d'autres sombres).
const readableTextColor = (hex) => {
  const c = (hex || '').replace('#', '')
  if (c.length !== 6) return '#0d1b2a'
  const r = parseInt(c.slice(0, 2), 16)
  const g = parseInt(c.slice(2, 4), 16)
  const b = parseInt(c.slice(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? '#0d1b2a' : '#ffffff'
}

// LAP-764 — assombrit une couleur hex (pour les dégradés / la bordure gauche).
const darken = (hex, amount = 0.18) => {
  const c = (hex || '').replace('#', '')
  if (c.length !== 6) return hex
  const ch = (i) =>
    Math.max(0, Math.round(parseInt(c.slice(i, i + 2), 16) * (1 - amount)))
  const h = (n) => n.toString(16).padStart(2, '0')
  return `#${h(ch(0))}${h(ch(2))}${h(ch(4))}`
}

// LAP-764 — palette douce et lisible. La plupart des événements générés n'ont
// pas de catégorie reconnue : on leur attribue une couleur STABLE (dérivée du
// nom/catégorie) pour que la semaine soit colorée et lisible au lieu d'être un
// aplat bleu-gris monotone.
const EVENT_PALETTE = [
  '#5a8dee', // bleu
  '#48a868', // vert
  '#e6a23c', // ambre
  '#9b6dde', // violet
  '#3fb6c4', // cyan
  '#e06b9c', // rose
  '#d8845a', // orange
  '#6c8ebf', // bleu-gris
]

const hashColor = (str) => {
  const s = str || ''
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return EVENT_PALETTE[h % EVENT_PALETTE.length]
}

// LAP-614b — libellé du bouton d'export selon la vue affichée. L'agenda montre
// un mois glissant, on l'aligne donc sur « le mois ».
const EXPORT_UNIT_BY_VIEW = {
  day: 'day',
  week: 'week',
  month: 'month',
  agenda: 'month',
  year: 'year',
}
const EXPORT_BTN_FALLBACK = {
  day: 'Exporter la journée',
  week: 'Exporter la semaine',
  month: 'Exporter le mois',
  year: "Exporter l'année",
}

const getCalendarViewFromSearch = (search) => {
  const view = new URLSearchParams(search).get('view')
  return ['day', 'week', 'month', 'agenda', 'year'].includes(view) ? view : 'month'
}

const getCalendarDateFromSearch = (search) => {
  const date = new URLSearchParams(search).get('date')
  if (!date) return new Date()

  const parsedDate = new Date(date)
  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate
}

const Schedule = () => {
  const { t, i18n } = useTranslation('layout') // LAP-733 (GE022)
  // react-big-calendar's culture prop also controls moment formatting; mirror
  // the active i18next language and fall back to 'en' for anything unknown.
  const supportedCultures = ['fr', 'en', 'de', 'it', 'es']
  const activeCulture = supportedCultures.includes((i18n.language || '').slice(0, 2))
    ? i18n.language.slice(0, 2)
    : 'en'
  const location = useLocation()
  const navigate = useNavigate() // LAP-765 — navigation vers /conseiller-temps
  // LAP-769 — pilote les modales "Générer planning" et "Paramétrer mes contraintes".
  const planningModalRef = React.useRef(null)
  const constraintsModalRef = React.useRef(null)
  // LAP-614b — fenêtre « Exporter vers un agenda externe » (période affichée).
  const exportModalRef = React.useRef(null)
  const allEvents = []
  const [finalEvents, setFinalEvents] = useState([])
  const [finalEventsTemp, setFinalEventsTemp] = useState([])
  const [taskList, setTaskList] = useState([])
  const [calendarView, setCalendarView] = useState(() => getCalendarViewFromSearch(location.search))
  const [calendarDate, setCalendarDate] = useState(() => getCalendarDateFromSearch(location.search))
  // LAP-614b — unité de période exportée, dérivée de la vue courante.
  const exportUnit = EXPORT_UNIT_BY_VIEW[calendarView] || 'month'
  const [slot, setSlot] = useState({})
  const [openQue, setOpenQue] = useState(false)
  const [propsQue, setPropsQue] = useState(false)
  const [dragTask, setDragTask] = useState(false)
  const [platform, setPlatform] = useState('all')
  const [sync, setSync] = useState('select')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [addGooglContact, setAddGooglContact] = useState(false)

  const [anchorEl, setAnchorEl] = useState(null)
  const openn = Boolean(anchorEl)
  const [openD, setOpenD] = useState(true) // For controlling the collapse state
  const [favoriteTasks, setFavoriteTasks] = useState([1, 2]) // Task IDs for favorites

  const handleDrawerToggle = () => {
    setOpenD(!openD)
  }

  const toggleFavorite = async (taskId) => {
    const res = await makePostRequest('/api/mestodo/task-favorit/', { id: taskId.id }, {}, true)
    if (res.status === 200) {
      getTaskList()
    }
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = (val = '') => {
    if (val) {
      syncCalender(val)
    } else {
      setAddGooglContact(false)
    }
    setAnchorEl(null)
  }
  // LAP-733 (GE022) — store the canonical platform key ('all' | 'google' | 'microsoft')
  // instead of the user-facing label so the UI can re-localize on language change.
  const [selectedCalender, setSelectedCalender] = useState('all')
  const [anchorEl2, setAnchorEl2] = useState(null)
  const openn2 = Boolean(anchorEl2)
  const handleClick2 = (event) => {
    setAnchorEl2(event.currentTarget)
  }
  // LAP-733 (GE022) — switched to canonical platform keys ('all'/'Google'/'Microsoft').
  // Backend filter values for Google/Microsoft were already in English-cased strings
  // (event_source matches 'Google' / 'Microsoft'), so only the 'all' selection was
  // mixing locale text with state.
  const handleClose2 = (val = '') => {
    if (val) {
      setSelectedCalender(val)
      if (val === 'all') {
        setFinalEvents(finalEventsTemp)
      }
      if (val === 'Google') {
        setFinalEvents(finalEventsTemp.filter((e) => e.event_source === val))
      }
      if (val === 'Microsoft') {
        setFinalEvents(finalEventsTemp.filter((e) => e.event_source === val))
      }
    }
    setAnchorEl2(null)
  }

  const createFixedEvents = (events, categoryDD) => {
    const fEvents = events.filter((x) => x.event_type === 'f')
    fEvents.forEach((e) => {
      if (e.years) {
        const startDate = new Date(`${e.day_num}-${e.month_num}-${e.years}`)
        e.start = startDate
        allEvents.push(e)
      } else {
        const startYear = 2000
        const lastYear = 2030
        // make events for all years in range 2000-2030
        for (let i = startYear; i < lastYear; i++) {
          let d = `${i}-${e.day_num}-${e.month_num}`
          let ed = `${i}-${e.day_num}-${e.month_num}`
          let cat = categoryDD.find((x) => x.id === e.category_id)
          let catName = (cat && cat.name) || ''
          let type = cat && cat.type.find((x) => x.id.toString() === e.type_id)
          let typeName = (type && type.name) || ''

          let event = {
            start: new Date(d),
            startDate: new Date(d),
            isAllDay: true,
            end: new Date(ed),
            endDate: new Date(ed),
            title: e.titre,
            location: e.lieux,
            type: typeName,
            category: catName,
            event_type: e.event_type,
            pays: e.pays,
            designation: e.designation,
            region: e.region,
          }
          allEvents.push(event)
        }
      }
    })
  }

  const syncCalender = async (val) => {
    const res = await makeGetRequest(`/api/user/sync-calender/${val}/`, {}, true)
    if (res.status === 201) {
      setAddGooglContact(true)
    } else if (res.status === 200 && res.data.push_results) {
      // LAP-614: Show bidirectional sync results
      const push = res.data.push_results
      if (push.total_processed > 0) {
        console.log(
          `Sync completed!\n` +
          `Imported events from ${val}\n` +
          `Pushed ${push.successful} Life events to ${val}\n` +
          `Excluded: ${push.excluded}, Failed: ${push.failed}`
        )
      }
    }
    getAllEvents()
  }
  
  const getAllEvents = async () => {
    // setFinalEvents([])
    //
    // Source d'événements : on utilisait /api/time/get-events (qui déplie les
    // récurrences côté serveur), mais cet endpoint est actuellement HS sur le
    // backend (HTTP 500 — le fix EventSerializer.get_participants_list dans
    // serializers.py n'a pas encore été déployé). Pour que le calendrier
    // fonctionne dès maintenant sans dépendre du déploiement backend, on lit
    // directement l'endpoint brut /api/time/events/.
    //
    // Conséquence temporaire : les événements RÉCURRENTS apparaissent
    // uniquement à leur date de base, pas à toutes leurs occurrences. Pour
    // les non-récurrents (la plupart des cas), l'affichage est complet.
    // Quand le backend sera à jour, on pourra rebasculer sur
    // /api/time/get-events pour retrouver le dépliage côté serveur.
    //
    // makeGetAllRequest (et pas ?limit=500) : LimitOffsetPagination tronque à
    // la première page — au-delà de 500 événements (sync Google/Microsoft…),
    // les événements les plus récents (planning généré inclus) n'arrivaient
    // JAMAIS au calendrier. On suit donc la pagination jusqu'au bout.
    const [resEvents, resCat] = await Promise.all([
      makeGetAllRequest('/api/time/events/', {}, true),
      makeGetRequest('/api/time/get-category-dd', {}, true),
    ])

    if (resEvents?.status !== 200 || resCat?.status !== 200) {
      console.error(
        '[Calendar] Échec de chargement des événements —',
        'events:', resEvents?.status,
        '| categories:', resCat?.status,
      )
      return
    }

    // /api/time/events/ est paginé (LimitOffsetPagination) : la réponse est
    // soit un tableau brut, soit { count, next, previous, results: [...] }.
    const raw = resEvents.data
    const events = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.results)
      ? raw.results
      : []
    console.info('[Calendar] Événements chargés :', events.length)
    // res2 garde son nom pour ne pas casser le bloc de parse historique
    // (qui s'appuie sur res2.data pour résoudre catégories et types).
    const res2 = resCat

    // filter appointment events
    const customEvents = events.filter((x) => x.event_type === 'a')
      let event_list = []
      customEvents.forEach((event) => {
        let sDate = event.start_date ? event.start_date.substring(0, 2) : ''
        let sMonth = event.start_date ? event.start_date.substring(3, 5) : ''
        let sYear = event.start_date ? event.start_date.substring(6, 10) : ''

        var [hour = '00', minute = '01'] = (event.start_time || '00:01').split(':')

        const startDate = new Date(
          Number(sYear),
          Number(sMonth) - 1,
          Number(sDate),
          Number(hour),
          Number(minute),
          0,
          0,
        )

        let eDate = event.end_date ? event.end_date.substring(0, 2) : ''
        let eMonth = event.end_date ? event.end_date.substring(3, 5) : ''
        let eYear = event.end_date ? event.end_date.substring(6, 10) : ''

        var [hour2 = '23', minute2 = '59'] = (event.end_time || '23:59').split(':')

        const endDate = new Date(
          Number(eYear),
          Number(eMonth) - 1,
          Number(eDate),
          Number(hour2),
          Number(minute2),
          0,
          0,
        )

        let cat = res2.data.find((x) => x.id === event.category_id)
        let catName = (cat && cat.name) || ''
        let type = cat && cat.type.find((x) => x.id.toString() === event.type_id)
        let typeName = (type && type.name) || ''

        event.title = event.titre
        event.start = startDate
        event.end = endDate
        event.startTime = event.start_time
        event.endTime = event.end_time
        event.location = event.lieux
        event.isAllDay = event.is_all_day
        event.type = typeName
        event.category = catName
        event_list.push(event)
        // allEvents.push(event)
      })
      // console.log(event_list)
      // createFixedEvents(events, res2.data)
      setFinalEvents(event_list)
      setFinalEventsTemp(event_list)
      // setDefaultView('year')
  }

  // Suppression optimiste : on retire l'événement de l'état local dès que le
  // DELETE a réussi, pour qu'il disparaisse IMMÉDIATEMENT du calendrier sans
  // attendre le refetch réseau (getAllEvents). On met à jour finalEvents (vue
  // courante) ET finalEventsTemp (source pour les filtres plateforme) afin de
  // rester cohérent avec handleClose2. La forme fonctionnelle setState(prev =>)
  // garantit un retrait correct même si la closure passée via messages est
  // ancienne.
  const removeEventLocally = (eventId) => {
    if (eventId === undefined || eventId === null) return
    const match = (e) => String(e.id) !== String(eventId)
    setFinalEvents((prev) => prev.filter(match))
    setFinalEventsTemp((prev) => prev.filter(match))
  }
  // console.log(finalEvents)
  useEffect(() => {
    getAllEvents()
    getTaskList()
  }, [])
  useEffect(() => {
    setCalendarView(getCalendarViewFromSearch(location.search))
    setCalendarDate(getCalendarDateFromSearch(location.search))
  }, [location.search])

  // La vue (jour/semaine/mois...) et la date sont déjà relues depuis l'URL au
  // montage. On les écrit AUSSI dans l'URL à chaque changement pour qu'un
  // refresh conserve la vue courante au lieu de repartir sur 'month'.
  const handleViewChange = (nextView) => {
    setCalendarView(nextView)
    const params = new URLSearchParams(location.search)
    params.set('view', nextView)
    navigate({ search: `?${params.toString()}` }, { replace: true })
  }
  const handleNavigateDate = (nextDate) => {
    setCalendarDate(nextDate)
    const params = new URLSearchParams(location.search)
    params.set('date', moment(nextDate).format('YYYY-MM-DD'))
    navigate({ search: `?${params.toString()}` }, { replace: true })
  }
  // useEffect(() => {
  //   getAllEvents()
  // }, [finalEvents])

  // const localizer = momentLocalizer(moment) // or globalizeLocalizer
  // localizer.formats.yearHeaderFormat = 'YYYY'
  const [showModal, setShowModal] = useState(false)
  const toggleModal = useCallback(() => {
    setShowModal(!showModal)
  })

  // Ajout rapide (shell mobile) : /MonCalendrier?action=new-event ouvre
  // directement la fenêtre de création d'événement (comme le bouton « + »).
  useUrlAction('new-event', () => setShowModal(true))

  const eventPropGetter = useCallback((event, start, end, isSelected) => {
    // LAP-764 — couleur de l'événement :
    //  - jour férié (type 'f')          → rouge distinct,
    //  - catégorie reconnue (Amis…)     → couleur de catégorie (getColor),
    //  - sinon (planning généré, tâches) → couleur STABLE dérivée du nom, pour
    //    que la semaine soit colorée et identifiable plutôt que monotone.
    const KNOWN_CATEGORIES = [
      'Amis',
      'Equipement',
      'Famille',
      'Maison',
      'Perso',
      'Travail',
    ]

    let baseColor
    if (event.event_type === 'f') {
      baseColor = '#c0504d'
    } else if (KNOWN_CATEGORIES.includes(event.type)) {
      baseColor = getColor(event.type)
    } else {
      baseColor = hashColor(event.category || event.type || event.title)
    }

    // LAP-614 : on conserve la différenciation interne/externe (Google/Microsoft)
    // via une bordure gauche pointillée + une légère transparence.
    const isExtern = event.calendar_type === 'extern'

    var style = {
      borderRadius: '8px',
      border: 'none',
      borderLeft: `4px solid ${darken(baseColor, 0.3)}`,
      color: readableTextColor(baseColor),
      fontWeight: 600,
      fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
      background: `linear-gradient(135deg, ${baseColor} 0%, ${darken(
        baseColor,
        0.16,
      )} 100%)`,
      opacity: isExtern ? 0.92 : 1,
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.28)',
    }

    if (isExtern) {
      style.borderLeftStyle = 'dashed'
    }

    return {
      style: style,
      className: event.type ? event.type.toLowerCase() : '',
    }
  })

  const onDropEventMove = async (props) => {
    const { event, start, end } = props
    let sDate = start ? start.getDate() : ''
    let sMonth = start ? start.getMonth() + 1 : ''
    let sYear = start ? start.getFullYear() : ''
    let sHour = start ? start.getHours() : ''
    let sMinute = start ? start.getMinutes() : ''

    const startDate = `${sYear}-${sMonth}-${sDate}`

    event.start_date = startDate
    event.start_time = `${sHour}:${sMinute}`

    let eDate = end ? end.getDate() : ''
    let eMonth = end ? end.getMonth() + 1 : ''
    let eYear = end ? end.getFullYear() : ''
    let eHour = start ? end.getHours() : ''
    let eMinute = start ? end.getMinutes() : ''

    const endDate = `${eYear}-${eMonth}-${eDate}`

    event.end_date = endDate
    event.end_time = `${eHour}:${eMinute}`

    const res1 = await makePutRequest(`/api/time/events/${event.id}/`, event, {}, true)
    // const res1 = await makePostRequest(`/api/time/events/`, event, {}, true)
    if (res1.status === 200) {
      getAllEvents()
    }
  }
  const onDropEventCopy = async (props) => {
    const { event, start, end } = props
    let sDate = start ? start.getDate() : ''
    let sMonth = start ? start.getMonth() + 1 : ''
    let sYear = start ? start.getFullYear() : ''
    let sHour = start ? start.getHours() : ''
    let sMinute = start ? start.getMinutes() : ''

    const startDate = `${sYear}-${sMonth}-${sDate}`

    event.start_date = startDate
    event.start_time = `${sHour}:${sMinute}`

    let eDate = end ? end.getDate() : ''
    let eMonth = end ? end.getMonth() + 1 : ''
    let eYear = end ? end.getFullYear() : ''
    let eHour = start ? end.getHours() : ''
    let eMinute = start ? end.getMinutes() : ''

    const endDate = `${eYear}-${eMonth}-${eDate}`

    event.end_date = endDate
    event.end_time = `${eHour}:${eMinute}`

    // const res1 = await makePutRequest(`/api/time/events/${event.id}/`, event, {}, true)
    const res1 = await makePostRequest(`/api/time/events/`, event, {}, true)
    if (res1.status === 201) {
      getAllEvents()
    }
  }
  const onDropResizeEvent = async (props) => {
    const { event, start, end } = props
    let sDate = start ? start.getDate() : ''
    let sMonth = start ? start.getMonth() + 1 : ''
    let sYear = start ? start.getFullYear() : ''
    let sHour = start ? start.getHours() : ''
    let sMinute = start ? start.getMinutes() : ''

    const startDate = `${sYear}-${sMonth}-${sDate}`

    event.start_date = startDate
    event.start_time = `${sHour}:${sMinute}`

    let eDate = end ? end.getDate() : ''
    let eMonth = end ? end.getMonth() + 1 : ''
    let eYear = end ? end.getFullYear() : ''
    let eHour = start ? end.getHours() : ''
    let eMinute = start ? end.getMinutes() : ''

    const endDate = `${eYear}-${eMonth}-${eDate}`

    event.end_date = endDate
    event.end_time = `${eHour}:${eMinute}`

    const res1 = await makePutRequest(`/api/time/events/${event.id}/`, event, {}, true)
    if (res1.status === 200) {
      getAllEvents()
    }
  }

  const onClickSlot = (slot) => {
    const elements = document.getElementsByClassName('ppevent')
    Array.from(elements).forEach((el) => el.click())
    setSlot(slot)
    setShowModal(true)
  }
  const closeOpen = () => {
    setAddGooglContact(false)
  }
  const getTaskList = async () => {
    const res = await makeGetRequest('/api/mestodo/getTasks/', {}, true)
    if (res.status === 200) {
      setTaskList(res.data)
    }
  }
  const handleQueClose = () => {
    setPropsQue(false)
    setOpenQue(false)
  }
  const handleDragTaskAdd = async (slotData, taskData) => {
    // LAP-764 — garde-fou : si aucune tâche n'est en cours de glissement, on
    // n'envoie rien (évite un événement vide au lâcher).
    if (!taskData || !slotData || !slotData.start) return

    const { start } = slotData
    // Fin = fin du créneau visé si fournie, sinon +1h par défaut.
    const end =
      slotData.end && slotData.end > start
        ? slotData.end
        : new Date(start.getTime() + 60 * 60 * 1000)

    const pad = (n) => String(n).padStart(2, '0')

    const startDate = `${start.getFullYear()}-${start.getMonth() + 1}-${start.getDate()}`
    const endDate = `${end.getFullYear()}-${end.getMonth() + 1}-${end.getDate()}`

    const payload = {
      EventData: {
        // LAP-764 — on respecte l'heure de dépôt au lieu d'un fixe 00:00–01:00,
        // pour que glisser une tâche la planifie réellement au bon créneau.
        start_time: `${pad(start.getHours())}:${pad(start.getMinutes())}`,
        end_time: `${pad(end.getHours())}:${pad(end.getMinutes())}`,
        start_date: startDate,
        end_date: endDate,
        titre: taskData.Nom,
      },
    }
    const response = await makePutRequest(
      urls.taskOperations + taskData.id + '/',
      payload,
      {},
      true,
    )
    if (response.status === 200) {
      getAllEvents()
      getTaskList()
    }
  }
  return (
    <>
      {openQue && (
        <Dialog
          open={true}
          onClose={handleQueClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{'   '}</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleQueClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'black',
            })}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <DialogContentText sx={{ px: 5, color: 'black', fontWeight: 700 }}>
              {/* LAP-733 (GE022) */}
              {t('time.dlgWhatTitle')}
            </DialogContentText>
          </DialogContent>
          <DialogActions
            sx={{ display: 'flex', justifyContent: 'center', '.MuiButton-root': { px: 5 } }}
          >
            <Button
              variant="contained"
              onClick={async () => {
                await onDropEventCopy(propsQue)
                handleQueClose()
              }}
            >
              {t('time.dlgCopy')}
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                await onDropEventMove(propsQue)
                handleQueClose()
              }}
            >
              {t('time.dlgMove')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {addGooglContact && <AddConnection updateList={closeOpen} openM={true} />}
      <Box sx={{ display: 'flex', width: '100%' }}>
        {/* Left Side Collapsible Drawer */}
        {!isMobile && (
          <Drawer
            // LAP-764 — translucent dark panel + subtle border, instead of the
            // old plain "gray" background, so the drawer matches the new UI.
            sx={{
              width: openD ? 300 : 8,
              minWidth: openD ? 300 : 8,
              flexShrink: 0,
              transition: '0.3s ease-in-out',
              '& .MuiDrawer-paper': {
                position: 'relative',
                overflow: 'unset',
                width: openD ? 300 : 8,
                minWidth: openD ? 300 : 8,
                transition: '0.3s ease-in-out',
                background:
                  'linear-gradient(180deg, rgba(11, 34, 69, 0.96) 0%, rgba(5, 7, 31, 0.96) 100%)',
                borderRight: '1px solid rgba(255,255,255,0.08)',
                color: 'white',
              },
              '& .MuiList-root': { height: '100%', overflowX: 'auto' },
            }}
            variant="persistent"
            anchor="left"
            open={isMobile ? false : true}
          >
            <Box
              sx={{
                position: 'absolute',
                right: '-28px',
                top: 12,
                cursor: 'pointer',
                bgcolor: 'rgba(11, 34, 69, 0.96)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderLeft: 'none',
                borderTopRightRadius: 999,
                borderBottomRightRadius: 999,
                width: 26,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 10px 24px rgba(0,0,0,0.32)',
              }}
              onClick={handleDrawerToggle}
            >
              {openD ? (
                <KeyboardArrowLeftIcon sx={{ color: 'white', fontSize: 20 }} />
              ) : (
                <KeyboardArrowRightIcon sx={{ color: 'white', fontSize: 20 }} />
              )}
            </Box>
            {openD && (
              <Box sx={{ height: '92vh', p: 1.25 }}>
                <List sx={{ '& .MuiListItem-root': { borderRadius: 2 } }}>
                  <ListItem
                    sx={{
                      bgcolor: 'rgba(124,198,178,0.14)',
                      border: '1px solid rgba(124,198,178,0.28)',
                      mb: 1,
                    }}
                  >
                    {/* LAP-733 (GE022) */}
                    <ListItemText
                      primary={t('time.sidebarFavorites')}
                      sx={{
                        '.MuiTypography-root': {
                          fontWeight: 850,
                          fontSize: '1rem',
                          color: '#ffffff',
                          letterSpacing: 0.2,
                        },
                      }}
                    />
                  </ListItem>
                  {taskList.map((task, index) => {
                    if (task.favorite) {
                      return (
                        <ListItem
                          draggable
                          onDragStart={() => {
                            // LAP-764 — on mémorise la tâche dès le début du
                            // glisser (onDragStart, fiable) plutôt que via onDrag,
                            // pour que le dépôt sur le calendrier la retrouve.
                            setDragTask(task)
                          }}
                          key={index}
                          sx={{
                            borderLeft: '4px solid #5aa1d8',
                            bgcolor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.06)',
                            borderLeftWidth: '4px',
                            borderLeftColor: '#5aa1d8',
                            marginBottom: '6px',
                            transition: 'background-color 120ms ease',
                            ':hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                          }}
                        >
                          <ListItemText
                            sx={{
                              cursor: 'grab',
                              '.MuiTypography-root': { fontWeight: 600 },
                            }}
                            primary={task.Nom}
                          />
                          <ListItemIcon sx={{ cursor: 'pointer', justifyContent: 'end', minWidth: 0 }}>
                            <TodayIcon
                              sx={{ color: 'rgba(255,255,255,0.78)' }}
                              onClick={() => setShowModal(true)}
                            />
                            <StarIcon
                              sx={{ color: '#f2b84b', ml: 1 }}
                              onClick={() => toggleFavorite(task)}
                            />
                          </ListItemIcon>
                        </ListItem>
                      )
                    } else return ''
                  })}

                  <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.08)' }} />
                  <ListItem
                    sx={{
                      bgcolor: 'rgba(90, 161, 216, 0.14)',
                      border: '1px solid rgba(90, 161, 216, 0.28)',
                      mb: 1,
                    }}
                  >
                    {/* LAP-733 (GE022) */}
                    <ListItemText
                      sx={{
                        '.MuiTypography-root': {
                          fontWeight: 850,
                          fontSize: '1rem',
                          color: '#ffffff',
                          letterSpacing: 0.2,
                        },
                      }}
                      primary={t('time.sidebarTaskSelection')}
                    />
                  </ListItem>
                  {taskList.map((task, index) => {
                    if (!task.Evenements && !task.favorite) {
                      return (
                        <ListItem
                          draggable
                          onDragStart={() => {
                            // LAP-764 — on mémorise la tâche dès le début du
                            // glisser (onDragStart, fiable) plutôt que via onDrag,
                            // pour que le dépôt sur le calendrier la retrouve.
                            setDragTask(task)
                          }}
                          key={index}
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.06)',
                            borderLeft: '4px solid #5aa1d8',
                            marginBottom: '6px',
                            transition: 'background-color 120ms ease',
                            ':hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                          }}
                        >
                          <ListItemText
                            sx={{
                              cursor: 'grab',
                              '.MuiTypography-root': { fontWeight: 600 },
                            }}
                            primary={task.Nom}
                          />
                          <ListItemIcon sx={{ cursor: 'pointer', justifyContent: 'end', minWidth: 0 }}>
                            <TodayIcon
                              sx={{ color: 'rgba(255,255,255,0.78)' }}
                              onClick={() => setShowModal(true)}
                            />
                            <StarBorderIcon
                              sx={{ color: 'rgba(255,255,255,0.78)', ml: 1 }}
                              onClick={() => toggleFavorite(task)}
                            />
                          </ListItemIcon>
                        </ListItem>
                      )
                    } else return ''
                  })}
                </List>
              </Box>
            )}
          </Drawer>
        )}

        {/* Right Side Content */}
        {/* <Box
          component="main"
          sx={{
            flexGrow: 1,

            p: 3,
          }}
        > */}
        {/* LAP-764 — root wrapper now uses the same dark gradient as /Menu
            so the calendar page visually belongs to the new Life UI. */}
        <Box
          sx={{
            minHeight: 'calc(100vh - 72px)',
            width: '100%',
            bgcolor: '#05071f',
            background:
              'linear-gradient(180deg, rgba(11, 34, 69, 0.76) 0%, #05071f 42%)',
            fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
            pb: 4,
          }}
        >
          <Container
            maxWidth="xl"
            className="scheduler-container"
            sx={{
              color: 'white',
              pt: { xs: 1.5, md: 2.5 },
              overflow: 'hidden',
            }}
          >
            {/* LAP-764 — polished header card (title + filters + actions),
                matching the /Menu (PlanMeal) layout: rounded, soft border,
                subtle shadow, pill-shaped buttons. */}
            <Box
              sx={{
                mb: 2,
                p: { xs: 1.5, md: 2 },
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 18px 44px rgba(0,0,0,0.22)',
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                gap={2}
              >
                <Box>
                  <Typography
                    sx={{
                      color: '#ffffff',
                      fontSize: { xs: 22, md: 28 },
                      fontWeight: 950,
                      lineHeight: 1.1,
                    }}
                  >
                    {/* LAP-764 — fallback strings keep the page working even
                        before new i18n keys are added. */}
                    {t('time.pageTitle', 'Mon calendrier')}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.62)',
                      fontSize: 14,
                      mt: 0.6,
                    }}
                  >
                    {t(
                      'time.pageHint',
                      'Organisez vos événements, synchronisez vos agendas et planifiez votre semaine.',
                    )}
                  </Typography>
                </Box>
                <Chip
                  icon={
                    <CalendarMonthIcon sx={{ color: '#7cc6b2 !important' }} />
                  }
                  label={moment(calendarDate)
                    .locale(activeCulture)
                    .format('MMMM YYYY')}
                  sx={{
                    bgcolor: 'rgba(124,198,178,0.14)',
                    color: '#ffffff',
                    border: '1px solid rgba(124,198,178,0.28)',
                    fontWeight: 850,
                    px: 1,
                    textTransform: 'capitalize',
                  }}
                />
              </Stack>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1.5}
                sx={{ mt: 2 }}
                flexWrap="wrap"
              >
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    startIcon={<SyncIcon />}
                    id="basic-button"
                    aria-controls={openn ? 'basic-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={openn ? 'true' : undefined}
                    onClick={handleClick}
                    sx={actionButtonSx}
                  >
                    {/* LAP-733 (GE022) */}
                    {t('time.btnSync')}
                  </Button>
                  <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={openn}
                    onClose={() => handleClose('')}
                    MenuListProps={{ 'aria-labelledby': 'basic-button' }}
                  >
                    <MenuItem onClick={() => handleClose('Google')}>Google</MenuItem>
                    <MenuItem onClick={() => handleClose('Microsoft')}>
                      Microsoft
                    </MenuItem>
                  </Menu>

                  {/* LAP-614b — export de la PÉRIODE AFFICHÉE vers un agenda
                      externe. Le libellé suit la vue courante (jour/semaine/
                      mois/année) pour que l'utilisateur sache ce qu'il envoie
                      avant même d'ouvrir la fenêtre de confirmation. */}
                  <Button
                    variant="outlined"
                    startIcon={<IosShareIcon />}
                    onClick={() => exportModalRef.current?.handleClickOpen()}
                    sx={actionButtonSx}
                  >
                    {t(`time.export.btn.${exportUnit}`, {
                      defaultValue: EXPORT_BTN_FALLBACK[exportUnit],
                    })}
                  </Button>

                  {/* LAP-614: Filter events per source — re-styled as a pill. */}
                  <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    id="basic-button2"
                    aria-controls={openn2 ? 'basic-menu2' : undefined}
                    aria-haspopup="true"
                    aria-expanded={openn2 ? 'true' : undefined}
                    onClick={handleClick2}
                    sx={actionButtonSx}
                  >
                    {/* LAP-733 (GE022) — Google/Microsoft are proper nouns,
                        only the "all" variant is localized. */}
                    {selectedCalender === 'all' ? t('time.btnAll') : selectedCalender}
                  </Button>
                  <Menu
                    id="basic-menu2"
                    anchorEl={anchorEl2}
                    open={openn2}
                    MenuListProps={{ 'aria-labelledby': 'basic-button2' }}
                  >
                    <MenuItem onClick={() => handleClose2('all')}>
                      {t('time.btnAll')}
                    </MenuItem>
                    <MenuItem onClick={() => handleClose2('Google')}>Google</MenuItem>
                    <MenuItem onClick={() => handleClose2('Microsoft')}>
                      Microsoft
                    </MenuItem>
                  </Menu>
                </Stack>

                <Stack direction="row" gap={1} flexWrap="wrap">
                  {/* LAP-765 — entrée vers le Conseiller Temps IA (maître du
                      temps). Le bouton vit dans le header pour rester
                      facilement accessible depuis le calendrier. */}
                  <Button
                    variant="outlined"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => navigate('/conseiller-temps')}
                    sx={actionButtonSx}
                  >
                    {t('time.btnTimeAdvisor', {
                      defaultValue: 'Maître du temps',
                    })}
                  </Button>
                  {/* LAP-769 — paramétrage des contraintes de vie. */}
                  <Button
                    variant="outlined"
                    startIcon={<TuneIcon />}
                    onClick={() => constraintsModalRef.current?.handleClickOpen()}
                    sx={actionButtonSx}
                  >
                    {t('time.planning.settings.btnOpen', {
                      defaultValue: 'Mes contraintes',
                    })}
                  </Button>
                  {/* LAP-769 — génération règle-based du planning de la semaine. */}
                  <Button
                    variant="outlined"
                    startIcon={<EventRepeatIcon />}
                    onClick={() => planningModalRef.current?.handleClickOpen()}
                    sx={actionButtonSx}
                  >
                    {t('time.planning.btnGenerate', {
                      defaultValue: 'Générer planning',
                    })}
                  </Button>
                  {/* LAP-769 — annuler en un coup un planning généré (après reload). */}
                  <Button
                    variant="outlined"
                    startIcon={<UndoIcon />}
                    onClick={() => planningModalRef.current?.openRuns()}
                    sx={actionButtonSx}
                  >
                    {t('time.planning.btnCancelRun', {
                      defaultValue: 'Annuler un planning',
                    })}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => toggleModal()}
                    sx={primaryActionButtonSx}
                  >
                    {/* LAP-733 (GE022) */}
                    {t('time.btnCreate')}
                  </Button>
                </Stack>
              </Stack>
            </Box>

            {/* LAP-764 — calendar itself sits inside a polished card to match
                the look of the weekly menu grid on /Menu. */}
            <Box
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.12)',
                boxShadow: '0 18px 50px rgba(0,0,0,0.28)',
                bgcolor: 'rgba(3, 8, 32, 0.7)',
                p: { xs: 1, md: 1.5 },
              }}
            >
            <EventActionsContext.Provider value={{ getAllEvents, removeEventLocally }}>
            <DnDCalendar
              className="muiCalanderlife"
              // LAP-733 (GE022) — culture is now driven by the active i18next
              // language so moment formats (dates, months, days) follow suit.
              culture={activeCulture}
              // dayLayoutAlgorithm={'no-overlap'}
              defaultView={calendarView}
              view={calendarView}
              date={calendarDate}
              onView={handleViewChange}
              onNavigate={handleNavigateDate}
              // LAP-764 — switched from a fixed 800px to a responsive viewport
              // height so the calendar scales correctly on small screens.
              style={{
                height: 'calc(100vh - 260px)',
                minHeight: 520,
                width: '100%',
              }}
              selectable={true}
              longPressThreshold={10}
              draggableAccessor={() => true}
              // LAP-764 — sans neutraliser le dragover, le navigateur refuse le
              // "drop" et le dépôt d'une tâche depuis le panneau ne se déclenche
              // jamais. On autorise donc explicitement le dépôt.
              onDragOver={(e) => {
                e.preventDefault()
              }}
              onDropFromOutside={(slot) => {
                handleDragTaskAdd(slot, dragTask)
              }}
              onSelectSlot={(slot) => {
                onClickSlot(slot)
              }}
              onEventDrop={(props) => {
                setOpenQue(true)
                setPropsQue(props)
              }}
              onEventResize={(props) => onDropResizeEvent(props)}
              onSelectEvent={(event) => {
                // const elements = document.getElementsByClassName('ppevent')
                // console.log(elements)
                // Array.from(elements).forEach((el) => {
                //   console.log(el)
                //   el.click()
                // })
                // console.log(event)
                // console.log(`fevent_${event.id}_${event.index}`)
                let evid = document.getElementById(`fevt${event.id}`)
                if (evid) {
                  evid.click()
                }
              }}
              timeslots={1}
              step={60}
              localizer={localizer}
              events={finalEvents}
              toolbar={true}
              views={{
                day: true,
                week: true,
                month: true,
                agenda: true,
                year: Year,
              }}
              components={{
                month: { event: MonthEvent },
                week: { event: Event },
                day: { event: Event },
                year: { event: YearEvent },
                agenda: { event: CustomAgendaEvent },
              }}
              // getAllEvents={getAllEvents}
              // LAP-733 (GE022) — toolbar messages now go through i18next.
              messages={{
                previous: '<',
                next: '>',
                today: t('time.toolbarToday'),
                month: t('time.toolbarMonth'),
                week: t('time.toolbarWeek'),
                day: t('time.toolbarDay'),
                agenda: t('time.toolbarAgenda'),
                year: t('time.toolbarYear'),
                getAllEvents: getAllEvents,
                removeEventLocally: removeEventLocally,
              }}
              eventPropGetter={eventPropGetter}
              formats={{
                dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
                  localizer.format(start, 'D', culture) +
                  ' - ' +
                  localizer.format(end, 'D MMMM YYYY', culture),
                dayHeaderFormat: (date, culture, localizer) =>
                  localizer.format(date, 'dddd D MMMM YYYY', culture),
                yearHeaderFormat: 'YYYY',
              }}
            />
            </EventActionsContext.Provider>
            </Box>
          </Container>
          {showModal && (
            <FormModal
              isOpen={showModal}
              getAllEvents={getAllEvents}
              toggleModal={toggleModal}
              /* LAP-733 (GE022) */
              title={t('time.eventDefaultTitle')}
              slot={slot}
            />
          )}
          {/* LAP-769 — modales planning : génération + paramétrage (montées pour les refs). */}
          <GeneratePlanningModal ref={planningModalRef} onComplete={getAllEvents} />
          <ConstraintsModal ref={constraintsModalRef} />
          {/* LAP-614b — la fenêtre reçoit la vue ET la date affichées : elle
              recalcule la période exacte (jour/semaine/mois/année) au moment
              de l'ouverture. */}
          <ExportCalendarModal
            ref={exportModalRef}
            view={calendarView}
            date={calendarDate}
            culture={activeCulture}
          />
        </Box>
      </Box>
      {/* </Box> */}
    </>
  )
}

export default Schedule
